import { getLocalizedValue } from "@repo/shared";
/**
 * Sustainability preferences widget (QP-179) — pure, shared by the advisor
 * wizard and the public flow renderer.
 *
 * Three sections (per the design):
 *   1. Gate — "Is sustainability important?" yes/no. "No" hides AND clears
 *      the downstream sections (the server clears them too).
 *   2. Preferences — required granulation 1-10 per configured scale criterion
 *      (SFDR + bars) and a yes/no toggle per product preference (default no).
 *   3. Portfolio alignment — product vs portfolio matching, with a required
 *      minimum share picked from fixed options (QL-696) when "portfolio"
 *      applies. QL-110: when the collection pins a single mode the radio is replaced by that mode's description — the client
 *      is not asked, but still supplies the threshold in pinned portfolio mode.
 *      QL-142: the product-preference section (ProductPreferencesPicker) may
 *      offer a "No preference" answer, mutually exclusive with the themes.
 *
 * The parent persists via onChange; the server normalizes and recomputes.
 */

import type {
  CollectionSustainabilityConfig,
  SfdrScoreValue,
  SustainabilityAlignmentMode,
  SustainabilityComponentInput,
  SustainabilityPreferences,
  SustainabilityThresholdPct,
} from "@repo/shared/types";
import {
  allowedAlignmentModes,
  effectiveAlignmentMode,
  effectiveProductPreferencesDisplay,
  normalizeThresholdPct,
  resolveProductPreferenceAnswer,
  SUSTAINABILITY_THRESHOLD_OPTIONS,
} from "@repo/shared/types";
import { useEffect, useRef, useState } from "react";
import { FlowComponentHeader } from "@/components/ui/flow-component-header";
import { Label } from "@/components/ui/label";
import { OptionRow } from "@/components/ui/option-row";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUserLanguage } from "@/lib/i18n/context";
import { useT } from "@/lib/i18n/use-t";
import type { ComponentStyleType } from "@/lib/types/flow";
import { InfoNote } from "./info-note";
import { ProductPreferencesPicker } from "./product-preferences-picker";
import { T } from "./sustainability-widget.i18n";

export type SustainabilityWidgetProps = {
  /** The flow collection's sustainability config (null = disabled → muted hint). */
  sustainabilityConfig: CollectionSustainabilityConfig | null;
  inputData?: string | null;
  onChange: (input: SustainabilityComponentInput) => void;
  disabled?: boolean;
  style?: ComponentStyleType;
  title?: string;
  description?: string;
  language?: string;
  hasValidationError?: boolean;
  /** QL-188 — group answering note shown under the header; null for non-group clients. */
  groupNote?: string | null;
};

type LocalPrefs = {
  /** Minimum-required SFDR score (6/8/9). */
  sfdr: SfdrScoreValue | null;
  /** bar key → minimum-required step key. */
  bars: Record<string, string | null>;
  productPreferences: Record<string, boolean>;
  noProductPreference: boolean;
  alignmentMode: SustainabilityAlignmentMode | null;
  /** One of the fixed options; a stored value outside them hydrates as null. */
  thresholdPct: SustainabilityThresholdPct | null;
};

const EMPTY_PREFS: LocalPrefs = {
  sfdr: null,
  bars: {},
  productPreferences: {},
  noProductPreference: false,
  alignmentMode: null,
  thresholdPct: null,
};

function parseInput(inputData: string | null | undefined): SustainabilityComponentInput | null {
  if (!inputData) return null;
  try {
    return JSON.parse(inputData) as SustainabilityComponentInput;
  } catch {
    return null;
  }
}

export function SustainabilityWidget({
  sustainabilityConfig,
  inputData,
  onChange,
  disabled = false,
  style = "none",
  title,
  description,
  language = "en_GB",
  hasValidationError = false,
  groupNote = null,
}: SustainabilityWidgetProps) {
  const { tenantDefaultLanguage } = useUserLanguage();
  const t = useT(T);

  const [important, setImportant] = useState<boolean | null>(null);
  const [prefs, setPrefs] = useState<LocalPrefs>(EMPTY_PREFS);
  const [isInitialized, setIsInitialized] = useState(false);
  // A stored threshold outside the fixed options (saved before QL-696). Shown
  // only on a locked instance, whose frozen snapshot and PDF still carry it.
  const [legacyThresholdPct, setLegacyThresholdPct] = useState<number | null>(null);

  // Keep the latest onChange without retriggering the save effect on parent re-renders.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastSavedRef = useRef<string | null>(null);

  // Hydrate local state from the stored inputData once.
  useEffect(() => {
    if (isInitialized) return;
    const parsed = parseInput(inputData);
    if (parsed) {
      setImportant(typeof parsed.important === "boolean" ? parsed.important : null);
      const p: SustainabilityPreferences = parsed.preferences ?? {};
      const thresholdPct = normalizeThresholdPct(p.thresholdPct);
      setLegacyThresholdPct(
        thresholdPct == null && typeof p.thresholdPct === "number" ? p.thresholdPct : null,
      );
      setPrefs({
        sfdr: p.sfdr ?? null,
        bars: Object.fromEntries(
          Object.entries(p.bars ?? {}).filter(([, v]) => v != null),
        ) as Record<string, string>,
        productPreferences: p.productPreferences ?? {},
        noProductPreference: p.noProductPreference === true,
        alignmentMode:
          p.alignmentMode === "individual" || p.alignmentMode === "portfolio"
            ? p.alignmentMode
            : null,
        thresholdPct,
      });
      // Built from the same normalized threshold as local state, so a legacy
      // free-form value does not look like an edit and trigger a save on load.
      lastSavedRef.current = JSON.stringify(
        buildInput(parsed.important ?? null, { ...p, thresholdPct }),
      );
    }
    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputData, isInitialized]);

  /**
   * The mode that actually applies (QL-110): the pinned one when the collection
   * allows a single mode, otherwise the client's answer. Used for BOTH rendering
   * and the saved payload, so hydration and the save effect agree and entering a
   * pinned-mode flow never triggers a spurious save.
   */
  function resolveMode(
    p: SustainabilityPreferences | LocalPrefs | null,
  ): SustainabilityAlignmentMode | null {
    return effectiveAlignmentMode(sustainabilityConfig, {
      alignmentMode: (p?.alignmentMode as SustainabilityAlignmentMode | null) ?? null,
    });
  }

  function buildInput(
    imp: boolean | null,
    p: SustainabilityPreferences | LocalPrefs | null,
  ): SustainabilityComponentInput {
    if (imp !== true || !p) return { important: imp, preferences: null };
    const mode = resolveMode(p);
    return {
      important: true,
      preferences: {
        sfdr: p.sfdr ?? null,
        bars: (p.bars as Record<string, string | null>) ?? {},
        productPreferences: p.productPreferences ?? {},
        // Through the shared rule (QL-142), so hydration and the save effect agree.
        noProductPreference: resolveProductPreferenceAnswer(sustainabilityConfig, p).noPreference,
        alignmentMode: mode,
        thresholdPct: mode === "portfolio" ? (p.thresholdPct ?? null) : null,
      },
    };
  }

  // Persist when the effective input changed (after hydration).
  useEffect(() => {
    if (!isInitialized || disabled) return;
    if (important === null && lastSavedRef.current === null) return; // untouched
    const input = buildInput(important, prefs);
    const serialized = JSON.stringify(input);
    if (serialized === lastSavedRef.current) return;
    lastSavedRef.current = serialized;
    onChangeRef.current(input);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [important, prefs, isInitialized, disabled]);

  const wrapperClass = hasValidationError
    ? "border border-destructive rounded-lg p-4"
    : style === "border"
      ? "border rounded-lg p-4"
      : style === "box"
        ? "bg-muted/30 rounded-lg p-4"
        : "";

  if (!sustainabilityConfig) {
    return (
      <div className="text-sm text-muted-foreground border border-dashed rounded-lg p-4">
        {t.notConfigured}
      </div>
    );
  }

  const config = sustainabilityConfig;
  // QL-110 — the collection decides whether the client is asked at all.
  const allowedModes = allowedAlignmentModes(config);
  const pinnedMode = allowedModes.length === 1 ? allowedModes[0] : null;
  const effectiveMode = resolveMode(prefs);

  const handleGateChange = (value: string) => {
    const next = value === "yes";
    setImportant(next);
    if (!next) setPrefs(EMPTY_PREFS); // "No" clears downstream sections
  };

  const scaleSelect = (
    id: string,
    steps: CollectionSustainabilityConfig["bars"][number]["steps"],
    value: string | null,
    onValue: (v: string) => void,
  ) => (
    <Select value={value ?? ""} onValueChange={(v) => onValue(v)} disabled={disabled}>
      <SelectTrigger id={id} className="w-56">
        <SelectValue placeholder={t.selectLevel} />
      </SelectTrigger>
      <SelectContent>
        {(steps ?? []).map((step) => (
          <SelectItem key={step.key} value={step.key}>
            {getLocalizedValue(step.labelI18n, language, tenantDefaultLanguage) || step.key}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const shownLegacyThreshold = disabled && prefs.thresholdPct == null ? legacyThresholdPct : null;
  const thresholdValue = prefs.thresholdPct ?? shownLegacyThreshold;

  return (
    <div className={wrapperClass} data-testid="sustainability-widget">
      <div className="space-y-6">
        <FlowComponentHeader title={title} description={description} />
        {groupNote && <InfoNote text={groupNote} />}
        {hasValidationError && <p className="text-sm text-destructive">{t.incompleteMessage}</p>}

        {/* 1 — Gate */}
        <div className="space-y-2">
          <Label className="text-base">{t.gateQuestion}</Label>
          <RadioGroup
            value={important === null ? "" : important ? "yes" : "no"}
            onValueChange={handleGateChange}
            disabled={disabled}
            className="flex gap-6"
          >
            <OptionRow>
              <RadioGroupItem value="yes" id="sustainability-important-yes" />
              <Label htmlFor="sustainability-important-yes" className="font-normal cursor-pointer">
                {t.yes}
              </Label>
            </OptionRow>
            <OptionRow>
              <RadioGroupItem value="no" id="sustainability-important-no" />
              <Label htmlFor="sustainability-important-no" className="font-normal cursor-pointer">
                {t.no}
              </Label>
            </OptionRow>
          </RadioGroup>
        </div>

        {important === true && (
          <>
            {/* 2 — Preferences (mirrors the collection config) */}
            <div className="space-y-4">
              <div>
                <Label className="text-base">{t.preferencesTitle}</Label>
                <p className="text-xs text-muted-foreground">{t.preferencesDescription}</p>
              </div>

              {config.sfdr && (
                <div className="space-y-2">
                  <Label htmlFor="sustainability-pref-sfdr">
                    {getLocalizedValue(config.sfdr.labelI18n, language, tenantDefaultLanguage)}{" "}
                    <span className="text-xs text-muted-foreground">({t.scaleHint})</span>
                  </Label>
                  <Select
                    value={prefs.sfdr != null ? String(prefs.sfdr) : ""}
                    onValueChange={(v) =>
                      setPrefs((p) => ({ ...p, sfdr: Number(v) as SfdrScoreValue }))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger id="sustainability-pref-sfdr" className="w-56">
                      <SelectValue placeholder={t.selectLevel} />
                    </SelectTrigger>
                    <SelectContent>
                      {(config.sfdr.scores ?? []).map((score) => (
                        <SelectItem key={score.value} value={String(score.value)}>
                          {getLocalizedValue(score.labelI18n, language, tenantDefaultLanguage) ||
                            String(score.value)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(config.bars ?? []).map((bar) => (
                <div key={bar.key} className="space-y-1">
                  <Label htmlFor={`sustainability-pref-bar-${bar.key}`}>
                    {getLocalizedValue(bar.labelI18n, language, tenantDefaultLanguage) || bar.key}{" "}
                    <span className="text-xs text-muted-foreground">({t.scaleHint})</span>
                  </Label>
                  {scaleSelect(
                    `sustainability-pref-bar-${bar.key}`,
                    bar.steps,
                    prefs.bars[bar.key] ?? null,
                    (v) => setPrefs((p) => ({ ...p, bars: { ...p.bars, [bar.key]: v } })),
                  )}
                </div>
              ))}

              {(config.productPreferences ?? []).length > 0 && (
                <ProductPreferencesPicker
                  preferences={config.productPreferences ?? []}
                  noPreferenceEnabled={resolveProductPreferenceAnswer(config, prefs).required}
                  display={effectiveProductPreferencesDisplay(config)}
                  value={{
                    productPreferences: prefs.productPreferences,
                    noProductPreference: prefs.noProductPreference,
                  }}
                  onChange={(next) => setPrefs((p) => ({ ...p, ...next }))}
                  title={t.productPreferencesTitle}
                  description={t.productPreferencesDescription}
                  language={language}
                  disabled={disabled}
                />
              )}
            </div>

            {/* 3 — Portfolio alignment */}
            <div className="space-y-2">
              <Label className="text-base">{t.alignmentTitle}</Label>
              {pinnedMode ? (
                // The collection pinned the rule — state it instead of asking,
                // reusing the description the radio option already carries.
                <p className="text-sm text-muted-foreground">
                  {pinnedMode === "individual"
                    ? t.alignmentIndividualDescription
                    : t.alignmentPortfolioDescription}
                </p>
              ) : (
                <RadioGroup
                  value={prefs.alignmentMode ?? ""}
                  onValueChange={(v) =>
                    setPrefs((p) => ({
                      ...p,
                      alignmentMode: v as SustainabilityAlignmentMode,
                    }))
                  }
                  disabled={disabled}
                >
                  <OptionRow align="start">
                    <RadioGroupItem
                      value="individual"
                      id="sustainability-alignment-individual"
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="sustainability-alignment-individual"
                      className="font-normal cursor-pointer"
                    >
                      {t.alignmentIndividual}
                      <span className="block text-xs text-muted-foreground">
                        {t.alignmentIndividualDescription}
                      </span>
                    </Label>
                  </OptionRow>
                  <OptionRow align="start">
                    <RadioGroupItem
                      value="portfolio"
                      id="sustainability-alignment-portfolio"
                      className="mt-0.5"
                    />
                    <Label
                      htmlFor="sustainability-alignment-portfolio"
                      className="font-normal cursor-pointer"
                    >
                      {t.alignmentPortfolio}
                      <span className="block text-xs text-muted-foreground">
                        {t.alignmentPortfolioDescription}
                      </span>
                    </Label>
                  </OptionRow>
                </RadioGroup>
              )}

              {effectiveMode === "portfolio" && (
                <div className="space-y-2 pt-1">
                  <Label htmlFor="sustainability-threshold">{t.thresholdLabel}</Label>
                  <Select
                    value={thresholdValue != null ? String(thresholdValue) : ""}
                    onValueChange={(v) =>
                      setPrefs((p) => ({ ...p, thresholdPct: normalizeThresholdPct(v) }))
                    }
                    disabled={disabled}
                  >
                    <SelectTrigger id="sustainability-threshold" className="w-56">
                      <SelectValue placeholder={t.selectThreshold} />
                    </SelectTrigger>
                    <SelectContent>
                      {shownLegacyThreshold != null && (
                        <SelectItem value={String(shownLegacyThreshold)}>
                          {t.thresholdOption(String(shownLegacyThreshold))}
                        </SelectItem>
                      )}
                      {SUSTAINABILITY_THRESHOLD_OPTIONS.map((pct) => (
                        <SelectItem key={pct} value={String(pct)}>
                          {t.thresholdOption(String(pct))}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
